import { format as formatDateFns, isValid, parseISO } from 'date-fns'
import type { Listing } from '~/models'

export function fmtAddress(
  property: Partial<{
    address?: string
    address2?: string
    city?: string
    county?: string
    stateAbbr: string
    postalCode: string
  }>,
  {
    oneLine,
    locationVisibility,
  }: {
    oneLine?: boolean
    locationVisibility?: Listing['locationVisibility']
  } = {}
) {
  if (locationVisibility === 'state')
    return `${property.stateAbbr ?? ''} (region)` // typing
  else if (locationVisibility === 'county') {
    return `${[property.county, property.stateAbbr].filter((v) => v).join(', ')} (region)`
  } else if (locationVisibility === 'city') {
    return `${[property.city, property.stateAbbr].filter((v) => v).join(', ')} (region)`
  }

  const lineOne = [property.address, property.address2]
    .filter((v) => v)
    .join(', ')
  const lineTwo = [
    property.city,
    property.postalCode
      ? `${property.stateAbbr} ${property.postalCode}`
      : property.stateAbbr,
  ]
    .filter((v) => v)
    .join(', ')
  if (oneLine) {
    return [lineOne, lineTwo].filter((v) => v).join(', ')
  }
  return [lineOne, lineTwo].filter((v) => v).join(', ')
}

export function fmtDate(
  dateInput: Date | string | null | undefined,
  formatStr?: string
) {
  if (!dateInput) return ''

  const date = typeof dateInput === 'string' ? parseISO(dateInput) : dateInput

  if (!isValid(date)) return ''

  if (!formatStr) {
    const parts = date.toISOString().split('T')[0].split('-')
    return `${parts[1]}/${parts[2]}/${parts[0]}`
  }

  return formatDateFns(date, formatStr)
}

export function fmtPhoneNumber(phoneNumber: string, extension?: string) {
  const formatted = phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
  return extension ? `${formatted} ext. ${extension}` : formatted
}

export function getPhoneHref(phoneNumber: string, extension?: string) {
  const digits = phoneNumber.replace(/\D/g, '')
  return extension ? `tel:+1${digits},${extension}` : `tel:+1${digits}`
}

export function fmtNumberAbbr(num?: number): string {
  if (!num) {
    return ''
  }
  const formatted = Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(num)
  if (formatted === '0') {
    return num.toString()
  }
  return formatted
}

export function fmtCurrencyAmount(num?: number): string {
  if (num === undefined || num === null) {
    return ''
  }

  const hasCents = !Number.isInteger(num)

  return Intl.NumberFormat('en-US', {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(num)
}

export function fmtName(
  firstName?: string | null,
  middleName?: string | null,
  lastName?: string | null
): string {
  return [firstName, middleName, lastName].filter((v) => v).join(' ')
}

export function fmtEmailName(
  email: string,
  ...name: (string | null | undefined)[]
): string {
  const filtered = name.filter((value) => value !== undefined && value !== null)

  if (filtered.length === 0) return email

  return `${fmtName(...filtered)} <${email}>`
}

export function fmtPlumbingCapacity(diameter?: number, capacity?: number) {
  let output = ''

  if (diameter) {
    output += `${fmtNumberAbbr(diameter)}"`
  }

  if (capacity) {
    if (output.length > 0) {
      output += ` / `
    }

    output += `${fmtNumberAbbr(capacity)} gal`
  }

  return output
}

export function fmtPower(volts?: number, amps?: number, phase?: string) {
  let power = ''

  if (volts) {
    power += `${volts} V`
  }

  if (amps) {
    if (power.length > 0) {
      power += ' @ '
    }

    power += `${amps} A`
  }

  if (phase) {
    if (power.length > 0) {
      power += ', '
    }

    power += phase
  }

  return power
}

export function fmtAcres(v: number) {
  return v
    .toLocaleString(undefined, { minimumFractionDigits: 4 })
    .replace(/(\.\d*?[1-9])0+$/, '$1')
    .replace('.0000', '')
}

export function pluralize(
  value: number,
  singular: string,
  plural?: string
): string {
  if (value === 1) return singular

  return plural ? plural : `${singular}s`
}

export function capitalize(value: string) {
  return value
    .split(' ')
    .map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    })
    .join(' ')
}

export function joinHtmlValues(...args: (string | undefined | null)[]) {
  const EMPTY_P =
    '<p>\\s*(?:<br\\s*\\/?>|<span>\\s*&nbsp;\\s*<\\/span>)\\s*<\\/p>'

  return args
    .map((v) => {
      return v
        ?.replace(new RegExp(`^(?:\\s*${EMPTY_P}\\s*)+`, 'gi'), '')
        .replace(new RegExp(`(?:\\s*${EMPTY_P}\\s*)+$`, 'gi'), '')
        .trim()
    })
    .filter(Boolean)
    .join('')
}
